import React, { useState, useEffect } from 'react';
import ExportButtons from './ExportButtons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement, PointElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend, ArcElement, BarElement, Filler
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

const tooltipStyle = {
  backgroundColor: 'rgba(18,18,30,0.95)',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  titleColor: '#fff',
  bodyColor: 'rgba(255,255,255,0.8)',
};

const gridStyle = { color: 'rgba(255,255,255,0.04)' };
const tickStyle = { color: 'rgba(255,255,255,0.4)', font: { size: 11 } };
const noGrid = { display: false };
const noBorder = { display: false };

const RevenueMonetization = () => {
  const [timeRange, setTimeRange] = useState('6m');

  const kpis = [
    { label: 'Total Revenue', value: '$1.2M', change: '+18.5%', positive: true, icon: 'fas fa-dollar-sign', color: '#f82581' },
    { label: 'IAP Success Rate', value: '99.4%', change: '+0.3%', positive: true, icon: 'fas fa-check-circle', color: '#10b981' },
    { label: 'Active VIP Subs', value: '19,210', change: '+3.2%', positive: true, icon: 'fas fa-crown', color: '#f59e0b' },
    { label: 'Coin Balance', value: '4.2M', change: '+5.1%', positive: true, icon: 'fas fa-coins', color: '#6366f1' },
    { label: 'ARPU', value: '$3.60', change: '+$0.25', positive: true, icon: 'fas fa-chart-line', color: '#8b5cf6' },
    { label: 'Refund Rate', value: '0.8%', change: '-0.2%', positive: true, icon: 'fas fa-undo', color: '#ef4444' },
  ];

  return (
    <div className="rm-container">
      <div className="rm-header">
        <div className="rm-header-left">
          <h4>Revenue & Monetization</h4>
          <p className="rm-subtitle">IAP validation, coin economy, subscriptions & payment health</p>
        </div>
        <div className="rm-header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ExportButtons
            data={[
              { metric: 'Total Revenue', value: '$1.2M', change: '+18.5%' },
              { metric: 'IAP Success Rate', value: '99.4%', change: '+0.3%' },
              { metric: 'Active VIP Subs', value: '19,210', change: '+3.2%' },
              { metric: 'Coin Balance', value: '4.2M', change: '+5.1%' },
              { metric: 'ARPU', value: '$3.60', change: '+$0.25' },
              { metric: 'Refund Rate', value: '0.8%', change: '-0.2%' },
              { metric: 'Stripe Success', value: '99.8%', change: 'Healthy' },
              { metric: 'Google Pay Success', value: '99.5%', change: 'Healthy' },
              { metric: 'Apple Pay Success', value: '99.7%', change: 'Healthy' },
            ]}
            headers={[
              { key: 'metric', label: 'Metric' }, { key: 'value', label: 'Value' }, { key: 'change', label: 'Change' },
            ]}
            title="Revenue & Monetization Report"
            filename="revenue_monetization"
          />
          <div className="rm-time-range">
            {['24h', '7d', '1m', '3m', '6m'].map(r => (
              <button key={r} className={`rm-range-btn ${timeRange === r ? 'active' : ''}`} onClick={() => setTimeRange(r)}>
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rm-metrics-grid">
        {kpis.map((kpi, i) => (
          <div key={i} className="rm-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="rm-metric-top">
              <div className="rm-metric-icon" style={{ background: kpi.color + '15', color: kpi.color }}><i className={kpi.icon}></i></div>
              <span className={`rm-metric-change ${kpi.positive ? 'up' : 'down'}`}><i className={`fas fa-arrow-${kpi.positive ? 'up' : 'down'}`}></i> {kpi.change}</span>
            </div>
            <div className="rm-metric-value">{kpi.value}</div>
            <div className="rm-metric-label">{kpi.label}</div>
          </div>
        ))}
      </div>

      <RevenueCharts />
      <IAPValidationTable />
      <CoinEconomyFlow />
      <SubscriptionSyncStatus />
      <PaymentGatewayHealth />
      <RevenueLeakageIndicators />
    </div>
  );
};

const RevenueCharts = () => {
  const revenueTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Coin Purchases',
        data: [240000, 280000, 320000, 380000, 420000, 480000],
        borderColor: '#f82581',
        backgroundColor: 'rgba(248,37,129,0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
      {
        label: 'VIP Subscriptions',
        data: [180000, 195000, 210000, 240000, 265000, 300000],
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99,102,241,0.05)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
      {
        label: 'Ad Revenue',
        data: [120000, 135000, 150000, 165000, 185000, 210000],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.05)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
      },
    ],
  };

  const chartOpts = (yCallback) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: yCallback }, grid: gridStyle, border: noBorder },
    },
  });

  return (
    <div className="rm-chart-card">
      <div className="rm-chart-head">
        <h6>Revenue Trend</h6>
        <div className="rm-chart-legend-inline">
          <span><span className="rm-dot" style={{ background: '#f82581' }}></span> Coin Purchases</span>
          <span><span className="rm-dot" style={{ background: '#6366f1' }}></span> VIP Subscriptions</span>
          <span><span className="rm-dot" style={{ background: '#10b981' }}></span> Ad Revenue</span>
        </div>
      </div>
      <div className="rm-chart-body" style={{ height: 280 }}>
        <Line data={revenueTrendData} options={chartOpts(v => '$' + (v / 1000).toFixed(0) + 'K')} />
      </div>
    </div>
  );
};

const IAPValidationTable = () => {
  const transactions = [
    { id: 'TXN-001', user: 'User#4521', type: 'VIP', amount: '$9.99', platform: 'iOS', status: 'success', timestamp: '2 min ago' },
    { id: 'TXN-002', user: 'User#7842', type: 'Coin', amount: '500', platform: 'Android', status: 'success', timestamp: '5 min ago' },
    { id: 'TXN-003', user: 'User#3456', type: 'VIP', amount: '$4.99', platform: 'iOS', status: 'pending', timestamp: '8 min ago' },
    { id: 'TXN-004', user: 'User#6789', type: 'Coin', amount: '1000', platform: 'Android', status: 'success', timestamp: '12 min ago' },
    { id: 'TXN-005', user: 'User#2341', type: 'VIP', amount: '$19.99', platform: 'iOS', status: 'failed', timestamp: '15 min ago' },
    { id: 'TXN-006', user: 'User#8765', type: 'Coin', amount: '2000', platform: 'Android', status: 'success', timestamp: '18 min ago' },
  ];

  const getStatusColor = (status) => {
    if (status === 'success') return '#10b981';
    if (status === 'failed') return '#ef4444';
    return '#f59e0b';
  };

  return (
    <div className="rm-chart-card">
      <div className="rm-chart-head">
        <h6><i className="fas fa-list"></i> IAP Validation Status</h6>
        <span className="rm-badge-sm">Recent Transactions</span>
      </div>
      <div className="rm-table-wrap">
        <table className="rm-table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>User</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Platform</th>
              <th>Status</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t, i) => (
              <tr key={i}>
                <td><code className="rm-code">{t.id}</code></td>
                <td>{t.user}</td>
                <td><span className="rm-badge" style={{ background: t.type === 'VIP' ? '#f59e0b15' : '#6366f115', color: t.type === 'VIP' ? '#f59e0b' : '#6366f1' }}>{t.type}</span></td>
                <td>{t.amount}</td>
                <td>{t.platform}</td>
                <td><span className="rm-status-badge" style={{ background: getStatusColor(t.status) + '15', color: getStatusColor(t.status) }}><i className={`fas fa-${t.status === 'success' ? 'check-circle' : t.status === 'failed' ? 'times-circle' : 'clock'}`}></i> {t.status}</span></td>
                <td className="rm-time">{t.timestamp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CoinEconomyFlow = () => {
  const flows = [
    { label: 'Coins Purchased', value: '1.2M', color: '#f82581', icon: 'fas fa-shopping-cart' },
    { label: 'Coins Spent\non Episodes', value: '680K', color: '#6366f1', icon: 'fas fa-video', subtext: '57%' },
    { label: 'Coins Gifted', value: '120K', color: '#10b981', icon: 'fas fa-gift', subtext: '10%' },
    { label: 'Coins Expired', value: '85K', color: '#ef4444', icon: 'fas fa-hourglass-end', subtext: '7%' },
    { label: 'Coins Balance', value: '315K', color: '#8b5cf6', icon: 'fas fa-wallet', subtext: '26%' },
  ];

  return (
    <div className="rm-section">
      <h6 className="rm-section-title"><i className="fas fa-code-branch"></i> Coin Economy Flow</h6>
      <div className="rm-flow-container">
        {flows.map((flow, i) => (
          <React.Fragment key={i}>
            <div className="rm-flow-card">
              <div className="rm-flow-icon" style={{ background: flow.color + '15', color: flow.color }}>
                <i className={flow.icon}></i>
              </div>
              <div className="rm-flow-value">{flow.value}</div>
              <div className="rm-flow-label">{flow.label}</div>
              {flow.subtext && <div className="rm-flow-subtext">{flow.subtext}</div>}
            </div>
            {i < flows.length - 1 && <div className="rm-flow-arrow"><i className="fas fa-arrow-right"></i></div>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const SubscriptionSyncStatus = () => {
  const syncMetrics = [
    { label: 'Sync Success Rate', value: '99.2%', change: '+0.1%', positive: true, icon: 'fas fa-sync', color: '#10b981' },
    { label: 'Avg Sync Delay', value: '1.2s', change: '-0.3s', positive: true, icon: 'fas fa-clock', color: '#6366f1' },
    { label: 'Active Multi-Device Users', value: '8,400', change: '+240', positive: true, icon: 'fas fa-mobile-alt', color: '#f82581' },
    { label: 'Sync Conflicts', value: '12/day', change: '-3', positive: true, icon: 'fas fa-exclamation-circle', color: '#f59e0b' },
  ];

  return (
    <div className="rm-section">
      <h6 className="rm-section-title"><i className="fas fa-mobile-alt"></i> Subscription Sync Status</h6>
      <div className="rm-sync-grid">
        {syncMetrics.map((m, i) => (
          <div key={i} className="rm-sync-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="rm-sync-header">
              <div className="rm-sync-icon" style={{ background: m.color + '15', color: m.color }}>
                <i className={m.icon}></i>
              </div>
              <span className={`rm-sync-change ${m.positive ? 'up' : 'down'}`}>
                <i className={`fas fa-arrow-${m.positive ? 'up' : 'down'}`}></i> {m.change}
              </span>
            </div>
            <div className="rm-sync-value">{m.value}</div>
            <div className="rm-sync-label">{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PaymentGatewayHealth = () => {
  const gateways = [
    { name: 'Apple IAP', status: 'operational', successRate: '99.8%', processingTime: '1.2s', failed24h: '1', volume24h: '2,841' },
    { name: 'Google Play Billing', status: 'operational', successRate: '99.5%', processingTime: '1.4s', failed24h: '4', volume24h: '2,156' },
    { name: 'Stripe', status: 'operational', successRate: '99.9%', processingTime: '2.1s', failed24h: '0', volume24h: '890' },
    { name: 'PayPal', status: 'operational', successRate: '99.3%', processingTime: '3.2s', failed24h: '3', volume24h: '420' },
    { name: 'Razorpay', status: 'operational', successRate: '99.6%', processingTime: '1.8s', failed24h: '2', volume24h: '680' },
  ];

  const getStatusColor = (status) => status === 'operational' ? '#10b981' : '#ef4444';

  return (
    <div className="rm-chart-card">
      <div className="rm-chart-head">
        <h6><i className="fas fa-credit-card"></i> Payment Gateway Health</h6>
      </div>
      <div className="rm-table-wrap">
        <table className="rm-table">
          <thead>
            <tr>
              <th>Gateway</th>
              <th>Status</th>
              <th>Success Rate</th>
              <th>Avg Processing Time</th>
              <th>Failed Txns (24h)</th>
              <th>Volume (24h)</th>
            </tr>
          </thead>
          <tbody>
            {gateways.map((g, i) => (
              <tr key={i}>
                <td><strong>{g.name}</strong></td>
                <td><span className="rm-status-dot" style={{ background: getStatusColor(g.status) }}></span> {g.status}</td>
                <td>{g.successRate}</td>
                <td>{g.processingTime}</td>
                <td><span className="rm-alert-count">{g.failed24h}</span></td>
                <td>{g.volume24h}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RevenueLeakageIndicators = () => {
  const alerts = [
    { label: 'Unvalidated Purchases', value: '23', severity: 'warning', icon: 'fas fa-exclamation-triangle' },
    { label: 'Duplicate Transactions', value: '5', severity: 'medium', icon: 'fas fa-copy' },
    { label: 'Failed Webhooks', value: '12', severity: 'warning', icon: 'fas fa-plug' },
    { label: 'Expired Trials Not Converted', value: '340', severity: 'info', icon: 'fas fa-info-circle' },
  ];

  const getSeverityColor = (severity) => {
    if (severity === 'warning') return '#f59e0b';
    if (severity === 'medium') return '#ef4444';
    return '#6366f1';
  };

  return (
    <div className="rm-section">
      <h6 className="rm-section-title"><i className="fas fa-chart-pie"></i> Revenue Leakage Indicators</h6>
      <div className="rm-leakage-grid">
        {alerts.map((a, i) => (
          <div key={i} className="rm-leakage-card" style={{ borderLeftColor: getSeverityColor(a.severity), animationDelay: `${i * 0.06}s` }}>
            <div className="rm-leakage-header">
              <div className="rm-leakage-icon" style={{ color: getSeverityColor(a.severity) }}>
                <i className={a.icon}></i>
              </div>
              <span className="rm-leakage-severity">{a.severity}</span>
            </div>
            <div className="rm-leakage-value">{a.value}</div>
            <div className="rm-leakage-label">{a.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RevenueMonetization;
